import React, { FunctionComponent, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { Button, Col, Row, Table } from "react-bootstrap";

import { useAuthentication } from "../../hooks/useAuthentication";
import { ContentLayout } from "../common/Layout";
import { useUserContext } from "../../contexts/UserContext";
import { useUsers } from "../../hooks/useUsers";
import FirebaseActions from "../../actions/firebase/FirebaseActions";
import { AccessControlModal } from "./AccessControlModal";
import { PlusToggle } from "./PlusToggle";

interface Props {
  isAuthenticated: boolean;
}

const Admin: FunctionComponent<Props> = ({ isAuthenticated }) => {
  const navigate = useNavigate();
  const { setActiveUser } = useUserContext();
  const [users, usersLoading, usersError] = useUsers();
  const [modalUserId, setModalUserId] = useState<string | null>(null);
  const [modalAccessControl, setModalAccessControl] = useState<Record<string, boolean>>({});
  const [isModalOpen, setModalOpen] = useState(false);

  useAuthentication(isAuthenticated);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/login");
    }
  }, [isAuthenticated, navigate]);

  if (usersError) {
    console.error(usersError);
  }

  const handleUserSelect = (userId: string): void => {
    const user = users.find((u) => u.id === userId);
    if (user) {
      setActiveUser(user);
      navigate(`/user/${userId}/dashboard`);
    }
  };

  const openModal = async (userId: string): Promise<void> => {
    try {
      const accessControlData = await FirebaseActions.getAccessControlData(userId);
      setModalUserId(userId);
      setModalAccessControl(accessControlData.allowedUsers);
      setModalOpen(true);
    } catch (error) {
      console.error("Failed to fetch access control data:", error);
    }
  };

  const closeModal = (): void => {
    setModalUserId(null);
    setModalOpen(false);
  };

  const handleSaveAccessControl = async (
    userId: string,
    updatedAccessControl: Record<string, boolean>
  ): Promise<void> => {
    try {
      await FirebaseActions.updateAccessControl(userId, updatedAccessControl);
      console.log("Access control updated successfully");
      closeModal();
    } catch (error) {
      console.error("Failed to update access control:", error);
    }
  };

  return (
    <ContentLayout title="Admin Dashboard" siteTitle="Admin Control Panel" isLoading={usersLoading}>
      <Row className="py-5">
        <Col>
          <Table responsive striped bordered hover>
            <thead>
              <tr>
                <th>Id</th>
                <th>Name</th>
                <th>E-Mail</th>
                <th>Spielgruppe Plus</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id}>
                  <td>{user.id}</td>
                  <td>{user.userProfile?.name}</td>
                  <td>{user.userProfile?.email || user.email}</td>
                  <td>
                    <PlusToggle userId={user.id} />
                  </td>
                  <td>
                    <Button className={"mb-2"} variant="primary" size="sm" onClick={() => handleUserSelect(user.id)}>
                      View Dashboard
                    </Button>
                    <Button variant="secondary" size="sm" onClick={() => openModal(user.id)}>
                      Manage Access
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Col>
      </Row>

      {modalUserId && (
        <AccessControlModal
          show={isModalOpen}
          onClose={closeModal}
          onSave={handleSaveAccessControl}
          userId={modalUserId}
          allowedUsers={modalAccessControl}
          allUsers={users}
        />
      )}
    </ContentLayout>
  );
};

export default Admin;
